import React, { useState, useRef, useEffect } from 'react';

/**
 * ZoomableSVGContainer - A wrapper for SVG charts that provides pinch-to-zoom and pan functionality
 * Optimized for mobile viewing with auto-zoom on mobile devices
 */
const ZoomableSVGContainer = ({ children, initialMobileZoom = 1.8 }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  
  // Touch handling state
  const lastTouchDistance = useRef(null);
  const lastTouchCenter = useRef(null);
  const isPanning = useRef(false);

  // Detect mobile and set initial zoom with centered positioning
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && containerRef.current) {
        setScale(initialMobileZoom);
        
        // Center the chart horizontally on mobile
        const containerWidth = containerRef.current.offsetWidth;
        
        // Calculate horizontal centering
        // When zoomed at 1.8x, content is 1.8x wider
        // We want to center it horizontally
        const offsetX = -(containerWidth * (initialMobileZoom - 1)) / 2;
        
        // For vertical, we want the title at the top
        // With zoom, we need minimal Y offset to keep title visible
        const offsetY = 0;
        
        setPosition({ 
          x: offsetX,
          y: offsetY
        });
      } else {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    };

    // Small delay to ensure container is mounted and sized
    const timer = setTimeout(checkMobile, 100);
    
    window.addEventListener('resize', checkMobile);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMobile);
    };
  }, [initialMobileZoom]);

  // Calculate distance between two touch points
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Calculate center point between two touches
  const getTouchCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Two-finger pinch gesture
      isPanning.current = false;
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      lastTouchDistance.current = distance;
      lastTouchCenter.current = getTouchCenter(e.touches[0], e.touches[1]);
    } else if (e.touches.length === 1) {
      // Single finger pan gesture
      isPanning.current = true;
      lastTouchCenter.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while zooming/panning

    if (e.touches.length === 2 && lastTouchDistance.current) {
      // Pinch zoom
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches[0], e.touches[1]);
      
      // Calculate scale change
      const scaleChange = distance / lastTouchDistance.current;
      const newScale = Math.min(Math.max(scale * scaleChange, 0.5), 4); // Limit zoom between 0.5x and 4x
      
      // Update scale
      setScale(newScale);
      lastTouchDistance.current = distance;
      lastTouchCenter.current = center;
      
    } else if (e.touches.length === 1 && isPanning.current && lastTouchCenter.current) {
      // Pan
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchCenter.current.x;
      const deltaY = touch.clientY - lastTouchCenter.current.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      
      lastTouchCenter.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    }
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      lastTouchDistance.current = null;
    }
    if (e.touches.length === 0) {
      isPanning.current = false;
      lastTouchCenter.current = null;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        touchAction: 'none', // Disable default touch behaviors
        position: 'relative',
        cursor: isPanning.current ? 'grabbing' : 'grab',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: '0 0',
          transition: isPanning.current ? 'none' : 'transform 0.1s ease-out',
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </div>
      
      {/* Show zoom indicator on mobile */}
      {isMobile && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: '600',
          pointerEvents: 'none',
          zIndex: 10,
        }}>
          {(scale * 100).toFixed(0)}%
        </div>
      )}
    </div>
  );
};

export default ZoomableSVGContainer;
